import React, { useState, useEffect } from "react";
import { Modal, Spin, Card, Row, Col, Table, Divider } from "antd";
import { useSelector } from "react-redux";
import { apiGetTransactionDetail } from "services/dashboardService";
import "./TransactionDetailModal.css";

const TransactionDetailModal = ({ visible, onClose, transactionId }) => {
  const [loading, setLoading] = useState(false);
  const [transactionData, setTransactionData] = useState(null);
  const userDataStore = useSelector((x) => x.userDataStore);

  useEffect(() => {
    if (visible && transactionId) {
      fetchTransactionDetail();
    } else if (!visible) {
      setTransactionData(null);
    }
  }, [visible, transactionId]);

  const fetchTransactionDetail = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("token", userDataStore.token);
      params.append("transaction_id", transactionId);
      const response = await apiGetTransactionDetail(params);
      console.log('TransactionDetailModal API response:', response);
      if (response) {
        // axiosPost unwraps data, so response is the detail object directly
        const detail = response.data ? response.data : response;
        if (!Array.isArray(detail.participants)) {
          detail.participants = [];
        }
        setTransactionData(detail);
      } else {
        setTransactionData(null);
      }
    } catch (error) {
      console.error("Failed to fetch transaction detail:", error);
      setTransactionData(null);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    const num = parseFloat(value) || 0;
    return "$" + num.toFixed(2);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return dateStr.replace(/-/g, "/");
  };

  const renderField = (label, value) => {
    if (value === null || value === undefined || value === "" || value === "0" || value === 0) return null;
    return (
      <div className="detail-field">
        <span className="detail-label">{label}:</span>
        <span className="detail-value">{value}</span>
      </div>
    );
  };

  const hasAnyValue = (...values) => values.some(v => v !== null && v !== undefined && v !== "" && v !== "0" && v !== 0);

  const programColumns = [
    {
      title: "Program",
      dataIndex: "program_name",
      key: "program_name"
    },
    {
      title: "Amount",
      dataIndex: "price_at_purchase",
      key: "price_at_purchase",
      render: (val) => formatCurrency(val)
    }
  ];

  return (
    <Modal
      title="Transaction Detail"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={900}
      // Removed dark theme className
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <Spin size="large" />
        </div>
      ) : transactionData ? (
        <div style={{ color: '#222' }}>
          <Row gutter={[16, 16]}>
            {/* Parent/Account Information */}
            {hasAnyValue(transactionData.p_first_name, transactionData.p_last_name, transactionData.p_email, transactionData.p_cell_phone, transactionData.p_home_phone, transactionData.p_relation) && (
              <Col span={12}>
                <Card title="Parent/Account Information" size="small">
                  {renderField("First Name", transactionData.p_first_name)}
                  {renderField("Last Name", transactionData.p_last_name)}
                  {renderField("Email", transactionData.p_email)}
                  {renderField("Cell Phone", transactionData.p_cell_phone)}
                  {renderField("Home Phone", transactionData.p_home_phone)}
                  {renderField("Relation", transactionData.p_relation)}
                </Card>
              </Col>
            )}

            {/* Billing Information */}
            {hasAnyValue(transactionData.bill_address, transactionData.bill_city, transactionData.bill_state, transactionData.bill_postal_code) && (
              <Col span={12}>
                <Card title="Billing Information" size="small">
                  {renderField("Street", transactionData.bill_address)}
                  {renderField("City", transactionData.bill_city)}
                  {renderField("State", transactionData.bill_state)}
                  {renderField("Zip", transactionData.bill_postal_code)}
                </Card>
              </Col>
            )}

            {/* Payment Information */}
            {hasAnyValue(transactionData.payment_method, transactionData.transaction_id, transactionData.total_checkout, transactionData.discount_code, transactionData.discount_amount, transactionData.reg_date) && (
              <Col span={12}>
                <Card title="Payment Information" size="small">
                  {renderField("Payment Method", transactionData.payment_method)}
                  {renderField("Transaction ID", transactionData.transaction_id)}
                  {renderField("Total", formatCurrency(transactionData.total_checkout))}
                  {renderField("Discount Code", transactionData.discount_code)}
                  {renderField("Discount Amount", transactionData.discount_amount ? formatCurrency(transactionData.discount_amount) : null)}
                  {renderField("Registration Date", formatDate(transactionData.reg_date))}
                </Card>
              </Col>
            )}

            {/* Emergency Contact */}
            {hasAnyValue(transactionData.c_first_name, transactionData.c_last_name, transactionData.c_cell_phone) && (
              <Col span={12}>
                <Card title="Emergency Contact" size="small">
                  {renderField("First Name", transactionData.c_first_name)}
                  {renderField("Last Name", transactionData.c_last_name)}
                  {renderField("Cell Phone", transactionData.c_cell_phone)}
                </Card>
              </Col>
            )}
          </Row>

          <Divider />

          {/* Registered Participants */}
          <h3>Registered Participants</h3>
          {transactionData.participants && transactionData.participants.length > 0 ? (
            transactionData.participants.map((participant, index) => (
              <Card
                key={participant.id || index}
                title={`${participant.camper_first_name} ${participant.camper_last_name}`}
                size="small"
                style={{ marginBottom: 16 }}
              >
                <Row gutter={[16, 8]}>
                  {hasAnyValue(participant.camper_age) && <Col span={8}>{renderField("Age", participant.camper_age)}</Col>}
                  {hasAnyValue(participant.camper_grade) && <Col span={8}>{renderField("Grade", participant.camper_grade == 0 ? "K" : participant.camper_grade)}</Col>}
                  {hasAnyValue(participant.camper_gender) && <Col span={8}>{renderField("Gender", participant.camper_gender)}</Col>}
                  {hasAnyValue(participant.camper_birthday) && <Col span={8}>{renderField("Birthday", formatDate(participant.camper_birthday))}</Col>}
                  {hasAnyValue(participant.camper_t_shirt) && <Col span={8}>{renderField("T-Shirt", participant.camper_t_shirt)}</Col>}
                  {hasAnyValue(participant.camper_email) && <Col span={8}>{renderField("Email", participant.camper_email)}</Col>}
                </Row>

                {participant.programs && participant.programs.length > 0 && (
                  <>
                    <Divider style={{ margin: "12px 0" }} />
                    <h4>Enrolled Programs</h4>
                    <Table
                      dataSource={participant.programs.map((p, i) => ({ ...p, key: i }))}
                      columns={programColumns}
                      pagination={false}
                      size="small"
                    />
                  </>
                )}
              </Card>
            ))
          ) : (
            <p>No participants found for this transaction.</p>
          )}
        </div>
      ) : (
        <p>No transaction data available.</p>
      )}
    </Modal>
  );
};

export default TransactionDetailModal;
